import { ChangeDetectorRef, Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { SelectProjectLocationsDialogComponent } from 'app/shared/components/select-project-locations-dialog/select-project-locations-dialog.component';
import { selectTimesheetLogs } from 'app/store/timesheet/timesheet.selector';
import { ROUTES } from 'app/utils/constants/routes.constants';
import { hashConfig } from 'app/utils/config/hash.config';
import { ClockOutDialogComponent } from './clock-out-dialog/clock-out-dialog.component';
import { clockOut } from 'app/store/timesheet/timesheet.action';
import { Actions, ofType } from '@ngrx/effects';
import { TimesheetTypes } from 'app/store/timesheet/timesheet.type';
import {
  checkIfCameraExists,
  getAllRouteParams,
} from 'app/utils/helpers/helper-functions';
import * as timesheetActions from 'app/store/timesheet/timesheet.action';
import { getProjectLocations } from 'app/store/project-locations/project-locations.action';
import { selectProjectLocations } from 'app/store/project-locations/project-locations.selector';
import { CapturePhotoDialog } from 'app/shared/components/capture-photo-dialog/capture-photo-dialog.component';
import { WebcamImage } from 'ngx-webcam';

@Component({
  selector: 'app-timesheet',
  templateUrl: './timesheet.component.html',
  styleUrls: ['./timesheet.component.scss'],
  standalone: false,
})
export class TimesheetComponent {
  private takePhotoDialogRef: MatDialogRef<CapturePhotoDialog> | null = null;
  companyID: any;
  currentProj$!: Observable<any>;
  currentProjectObj: any;
  isLoading = false;
  webcamImage: WebcamImage | null = null;
  fileName: string = '';
  hasCamera: boolean = false;
  constructor(
    public dialog: MatDialog,
    private store: Store,
    private router: Router,
    private actions: Actions,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.hasCamera = await checkIfCameraExists();
    const routeParams = getAllRouteParams(this.route);
    this.store.dispatch(getProjectLocations());
    this.companyID = routeParams['companyID'];
    this.currentProj$ = this.store.select(selectTimesheetLogs).pipe(
      filter((data): data is any[] => Array.isArray(data) && data.length > 0),
      map((data) => data.find((item) => item.clock_out === null)),
      catchError((err) => {
        return of([]);
      }),
    );
    this.currentProj$
      .pipe(
        filter((proj) => !!proj?.projectID),
        switchMap((proj) =>
          this.store
            .select(selectProjectLocations)
            .pipe(
              map((projects) =>
                Array.isArray(projects)
                  ? projects.find((p) => p.projectID === proj.projectID)
                  : null,
              ),
            ),
        ),
      )
      .subscribe((currentProjectObj) => {
        this.currentProjectObj = currentProjectObj;
      });
  }

  onHandleClockIn() {
    const loading$ = new BehaviorSubject<boolean>(true);
    this.dialog.open(SelectProjectLocationsDialogComponent, {
      data: {
        loading$,
      },
    });

    loading$.subscribe((isLoading) => {
      setTimeout(() => {
        this.isLoading = isLoading;
        this.cdr.detectChanges();
      });
    });
  }

  onHandleClockOut() {
    if (this.currentProjectObj?.project_selfie_enabled && this.hasCamera) {
      if (this.takePhotoDialogRef && this.takePhotoDialogRef.getState() !== 0) {
        return;
      }
      const takePhotoDialog = this.dialog.open(CapturePhotoDialog, {
        disableClose: true,
      });

      takePhotoDialog.afterClosed().subscribe((result) => {
        if (!result) return;
        this.webcamImage = result.webcamImage;
        this.fileName = result.fileName;
        this.openClockOutDialog();
      });
    } else {
      this.openClockOutDialog();
    }
  }

  openClockOutDialog() {
    const confirmDialog = this.dialog.open(ClockOutDialogComponent, {
      disableClose: true,
      data: {
        webcamImage: this.webcamImage,
        fileName: this.fileName,
      },
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result) {
        let timesheetID: any;
        this.currentProj$.subscribe((data) => {
          timesheetID = data?.timesheetID;
        });
        if (result.hasBreak) {
          this.router.navigate(
            [
              `${this.companyID}/${ROUTES.addBreakWithId(
                hashConfig.encrypt(timesheetID),
              )}`,
            ],
            {
              queryParams: {
                mood: result.mood,
                note: result.note,
                fileName: this.fileName,
              },
            },
          );
        } else {
          this.isLoading = true;
          this.cdr.detectChanges();
          this.store.dispatch(
            clockOut({
              mood: result.mood,
              timesheetID,
              ...(result.note && { note: result.note }),
              ...(this.fileName && { clockOutSelfie: this.fileName }),
            }),
          );

          this.actions
            .pipe(
              ofType(
                TimesheetTypes.POST_CLOCK_OUT_SUCCESS,
                TimesheetTypes.POST_CLOCK_OUT_CANCEL,
              ),
              tap(() => {
                this.store.dispatch(timesheetActions.getTimesheetLogs());
                this.isLoading = false;
                this.cdr.detectChanges();
              }),
            )
            .subscribe();
        }
      }
    });
  }
}
